<template>
  <div class="container-fluid py-4">
    <h4 class="mb-4">{{ $t('golf.settings') }}</h4>

    <!-- Courses -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">{{ $t('golf.courses') }}</h6>
          <button class="btn btn-sm btn-success" @click="showCreateCourse = true">
            <i class="material-icons" style="font-size:16px;vertical-align:middle;">add</i> {{ $t('golf.newCourse') }}
          </button>
        </div>

        <div v-if="courses.length === 0" class="text-muted text-center py-3">{{ $t('golf.noCourses') }}</div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead><tr>
              <th>{{ $t('golf.name') }}</th><th>{{ $t('golf.holes') }}</th><th>{{ $t('golf.coordinates') }}</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="c in courses" :key="c.id">
                <td>{{ c.name }}</td>
                <td>{{ c.holeCount }} / {{ c.numberOfHoles }}</td>
                <td>
                  <small v-if="c.latitude">{{ c.latitude?.toFixed(4) }}, {{ c.longitude?.toFixed(4) }}</small>
                  <small v-else class="text-muted">–</small>
                </td>
                <td>
                  <button v-if="c.holeCount === 0" class="btn btn-xs btn-outline-success" @click="initializeHoles(c)">
                    {{ $t('golf.initializeHoles') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Create course modal -->
        <div v-if="showCreateCourse" class="mt-3 p-3 border rounded bg-light">
          <h6 class="fw-bold">{{ $t('golf.newGolfCourse') }}</h6>
          <div class="row g-2">
            <div class="col-md-4">
              <label class="form-label small">{{ $t('golf.name') }}</label>
              <input v-model="newCourse.name" type="text" class="form-control form-control-sm" />
            </div>
            <div class="col-md-2">
              <label class="form-label small">{{ $t('golf.numberOfHoles') }}</label>
              <input v-model.number="newCourse.numberOfHoles" type="number" class="form-control form-control-sm" />
            </div>
            <div class="col-md-3">
              <label class="form-label small">{{ $t('golf.latitude') }}</label>
              <input v-model.number="newCourse.latitude" type="number" step="0.0001" class="form-control form-control-sm" />
            </div>
            <div class="col-md-3">
              <label class="form-label small">{{ $t('golf.longitude') }}</label>
              <input v-model.number="newCourse.longitude" type="number" step="0.0001" class="form-control form-control-sm" />
            </div>
          </div>
          <div class="mt-2">
            <button class="btn btn-sm btn-success me-2" @click="createCourse">{{ $t('golf.save') }}</button>
            <button class="btn btn-sm btn-outline-secondary" @click="showCreateCourse = false">{{ $t('golf.cancel') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Default Course -->
    <div class="card mb-4" v-if="courses.length > 1">
      <div class="card-body">
        <h6 class="fw-bold mb-1">{{ $t('golf.defaultCourse') }}</h6>
        <small class="text-muted d-block mb-3">{{ $t('golf.defaultCourseHint') }}</small>
        <div class="row g-2 align-items-end">
          <div class="col-md-4">
            <select v-model="defaultCourseId" class="form-select form-select-sm">
              <option :value="null">-- {{ $t('golf.noneSelected') }} --</option>
              <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <button class="btn btn-sm btn-success w-100" :disabled="savingDefault" @click="saveDefaultCourse">
              <span v-if="savingDefault" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('golf.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Holes (per selected course) -->
    <div class="card mb-4" v-if="courses.length > 0">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">{{ $t('golf.holes') }}</h6>
          <select v-model="selectedCourseId" class="form-select form-select-sm" style="width: auto;">
            <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>

        <div v-if="holes.length === 0" class="text-muted text-center py-3">{{ $t('golf.noHolesOnCourse') }}</div>
        <div v-else>
          <div v-for="h in holes" :key="h.id" class="border rounded mb-2">
            <!-- Hole header row -->
            <div class="d-flex align-items-center px-3 py-2 bg-light" style="cursor:pointer;" @click="toggleHole(h)">
              <i class="material-icons me-2" style="font-size:18px;transition:transform 0.2s;" :style="{ transform: expandedHoleId === h.id ? 'rotate(90deg)' : '' }">chevron_right</i>
              <strong class="me-3">{{ $t('golf.hole') }} {{ h.holeNumber }}</strong>
              <span class="text-muted small me-3" v-if="h._name">{{ h._name }}</span>
              <span class="badge bg-secondary me-2" v-if="h._par">{{ $t('golf.par') }} {{ h._par }}</span>
              <span class="badge bg-info text-dark me-2" v-if="h._teeCount">{{ h._teeCount }} {{ h._teeCount === 1 ? 'tee' : 'tees' }}</span>
              <div class="ms-auto">
                <router-link :to="`/golf/hole/${h.id}`" class="btn btn-xs btn-outline-dark" @click.stop>
                  <i class="material-icons" style="font-size:14px;">visibility</i>
                </router-link>
              </div>
            </div>

            <!-- Expanded content -->
            <div v-if="expandedHoleId === h.id" class="px-3 pb-3 pt-2">
              <!-- Basic info -->
              <div class="row g-2 mb-3">
                <div class="col-md-3">
                  <label class="form-label small mb-0">{{ $t('golf.name') }}</label>
                  <input v-model="h._name" type="text" class="form-control form-control-sm" @blur="updateHole(h)" />
                </div>
                <div class="col-md-2">
                  <label class="form-label small mb-0">{{ $t('golf.par') }}</label>
                  <input v-model.number="h._par" type="number" class="form-control form-control-sm" @blur="updateHole(h)" />
                </div>
                <div class="col-md-2">
                  <label class="form-label small mb-0">{{ $t('golf.handicapIndex') }}</label>
                  <input v-model.number="h._handicapIndex" type="number" class="form-control form-control-sm" @blur="updateHole(h)" />
                </div>
              </div>

              <!-- Green & Fairway collapsible -->
              <div class="border rounded mb-2">
                <div class="d-flex align-items-center px-3 py-2" style="cursor:pointer;" @click="toggleSection(h, 'surfaces')">
                  <i class="material-icons me-2" style="font-size:16px;transition:transform 0.2s;" :style="{ transform: isSectionOpen(h, 'surfaces') ? 'rotate(90deg)' : '' }">chevron_right</i>
                  <span class="fw-bold small">{{ $t('golf.green') }} &amp; {{ $t('golf.fairwayArea') }}</span>
                </div>
                <div v-if="isSectionOpen(h, 'surfaces')" class="px-3 pb-3">
                  <div class="row g-2">
                    <div class="col-md-4">
                      <label class="form-label small mb-0">{{ $t('golf.green') }} (m²)</label>
                      <input v-model.number="h._greenAreaM2" type="number" class="form-control form-control-sm" @blur="updateHole(h)" />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small mb-0">{{ $t('golf.fairwayArea') }} (m²)</label>
                      <input v-model.number="h._fairwayAreaM2" type="number" class="form-control form-control-sm" @blur="updateHole(h)" />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small mb-0">{{ $t('golf.fairwayLength') }} (m)</label>
                      <input v-model.number="h._fairwayLengthM" type="number" class="form-control form-control-sm" @blur="updateHole(h)" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tees collapsible -->
              <div class="border rounded">
                <div class="d-flex align-items-center px-3 py-2" style="cursor:pointer;" @click="toggleSection(h, 'tees'); if (isSectionOpen(h, 'tees')) loadTees(h);">
                  <i class="material-icons me-2" style="font-size:16px;transition:transform 0.2s;" :style="{ transform: isSectionOpen(h, 'tees') ? 'rotate(90deg)' : '' }">chevron_right</i>
                  <span class="fw-bold small">{{ $t('golf.teeSet') }}</span>
                  <span class="badge bg-secondary ms-2">{{ h._teeCount }}</span>
                </div>
                <div v-if="isSectionOpen(h, 'tees')" class="px-3 pb-3">
                  <div v-if="holeTees[h.id] && holeTees[h.id].length" class="table-responsive mb-3">
                    <table class="table table-sm mb-0">
                      <thead><tr><th>{{ $t('golf.name') }}</th><th>{{ $t('golf.distance') }} (m)</th><th>{{ $t('golf.teeAreaM2') }}</th><th></th></tr></thead>
                      <tbody>
                        <tr v-for="t in holeTees[h.id]" :key="t.id">
                          <td>{{ t.name }}</td>
                          <td>{{ t.distanceM }}</td>
                          <td>{{ t.areaM2 || '–' }}</td>
                          <td>
                            <button class="btn btn-xs btn-outline-danger" @click="deleteTee(h, t)">
                              <i class="material-icons" style="font-size:14px;">delete</i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else-if="holeTees[h.id] && holeTees[h.id].length === 0" class="text-muted small mb-2">{{ $t('golf.noTees') }}</div>

                  <div class="row g-2 align-items-end">
                    <div class="col-md-3">
                      <label class="form-label small mb-0">{{ $t('golf.teeNameHint') }}</label>
                      <input v-model="newTee.name" type="text" class="form-control form-control-sm" />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small mb-0">{{ $t('golf.distance') }} (m)</label>
                      <input v-model.number="newTee.distanceM" type="number" class="form-control form-control-sm" />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small mb-0">{{ $t('golf.teeAreaM2') }}</label>
                      <input v-model.number="newTee.areaM2" type="number" class="form-control form-control-sm" />
                    </div>
                    <div class="col-md-3">
                      <button class="btn btn-sm btn-success w-100" @click="createTee(h)">{{ $t('golf.addTee') }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fertilizer Products -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">{{ $t('golf.fertilizerProducts') }}</h6>
        </div>

        <div v-if="fertilizerProducts.length === 0" class="text-muted text-center py-3">{{ $t('golf.noFertilizerProducts') }}</div>
        <div v-else class="mb-3">
          <div v-for="p in fertilizerProducts" :key="p.id" class="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-1">
            <span>{{ p.name }}</span>
            <button class="btn btn-xs btn-outline-danger" @click="deleteFertilizerProduct(p)">
              <i class="material-icons" style="font-size:14px;">delete</i>
            </button>
          </div>
        </div>

        <div class="row g-2 align-items-end">
          <div class="col-md-6">
            <label class="form-label small mb-0">{{ $t('golf.productName') }}</label>
            <input v-model="newFertilizerProductName" type="text" class="form-control form-control-sm" />
          </div>
          <div class="col-md-3">
            <button class="btn btn-sm btn-success w-100" @click="createFertilizerProduct">
              <i class="material-icons" style="font-size:16px;vertical-align:middle;">add</i> {{ $t('golf.addProduct') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Weather Stations -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">{{ $t('golf.weatherStations') }}</h6>
          <button class="btn btn-sm btn-success" @click="showCreateStation = true">
            <i class="material-icons" style="font-size:16px;vertical-align:middle;">add</i> {{ $t('golf.newWeatherStation') }}
          </button>
        </div>

        <div v-if="stations.length === 0" class="text-muted text-center py-3">{{ $t('golf.noWeatherStations') }}</div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead><tr>
              <th>{{ $t('golf.name') }}</th><th>{{ $t('golf.weatherProvider') }}</th><th>Station ID</th><th>API URL</th><th>{{ $t('golf.coordinates') }}</th><th>{{ $t('golf.course') }}</th><th>{{ $t('golf.activeLabel') }}</th>
            </tr></thead>
            <tbody>
              <tr v-for="s in stations" :key="s.id" @click="editStation(s)" style="cursor:pointer;">
                <td>{{ s.name }}</td>
                <td>{{ providerLabel(s.provider) }}</td>
                <td><code>{{ s.stationId }}</code></td>
                <td><small>{{ s.baseApiUrl }}</small></td>
                <td><small>{{ s.geocode || '–' }}</small></td>
                <td>{{ courseNameById(s.golfCourseId) }}</td>
                <td>
                  <span class="badge" :class="s.isActive ? 'bg-success' : 'bg-secondary'">
                    {{ s.isActive ? $t('golf.yes') : $t('golf.no') }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Create/Edit station form -->
        <div v-if="showCreateStation || editingStation" class="mt-3 p-3 border rounded bg-light">
          <h6 class="fw-bold">{{ editingStation ? $t('golf.editWeatherStation') : $t('golf.newWeatherStation') }}</h6>
          <div class="row g-2">
            <div class="col-md-3">
              <label class="form-label small">{{ $t('golf.name') }}</label>
              <input v-model="stationForm.name" type="text" class="form-control form-control-sm" />
            </div>
            <div class="col-md-3">
              <label class="form-label small">{{ $t('golf.weatherProvider') }}</label>
              <select v-model.number="stationForm.provider" class="form-select form-select-sm" @change="onProviderChange">
                <option :value="1">{{ $t('golf.weatherProviderWeatherCompany') }}</option>
                <option :value="2">{{ $t('golf.weatherProviderVedur') }}</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small">Station ID</label>
              <input v-model="stationForm.stationId" type="text" class="form-control form-control-sm" />
            </div>
            <div class="col-md-3">
              <label class="form-label small">{{ $t('golf.course') }}</label>
              <select v-model="stationForm.golfCourseId" class="form-select form-select-sm">
                <option :value="null">-- {{ $t('golf.noneSelected') }} --</option>
                <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label small">API Base URL</label>
              <input v-model="stationForm.baseApiUrl" type="text" class="form-control form-control-sm" />
            </div>
            <div v-if="stationForm.provider === 1" class="col-md-3">
              <label class="form-label small">API Key</label>
              <input v-model="stationForm.apiKey" type="text" class="form-control form-control-sm" :placeholder="editingStation ? $t('golf.leaveBlankToKeep') : ''" />
            </div>
            <div v-if="stationForm.provider === 1" class="col-md-3">
              <label class="form-label small">Geocode (lat,lng)</label>
              <input v-model="stationForm.geocode" type="text" class="form-control form-control-sm" placeholder="65.68,-18.11" />
            </div>
          </div>
          <div class="mt-2 d-flex align-items-center">
            <button class="btn btn-sm btn-success me-2" @click="saveStation">{{ $t('golf.save') }}</button>
            <button class="btn btn-sm btn-outline-secondary me-3" @click="cancelStation">{{ $t('golf.cancel') }}</button>
            <div v-if="editingStation" class="form-check">
              <input v-model="stationForm.isActive" type="checkbox" class="form-check-input" id="stationActive" />
              <label for="stationActive" class="form-check-label small">{{ $t('golf.activeLabel') }}</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { golfApi as api } from "../../services/api";
import mainApi from "../../services/api";
import { getCustomerConfig, getCustomerConfigTypeId, loadCustomerConfigs } from '../Settings/customerConfigHelper';

export default {
  data() {
    return {
      courses: [],
      holes: [],
      stations: [],
      selectedCourseId: null,
      showCreateCourse: false,
      newCourse: { name: '', numberOfHoles: 18, latitude: null, longitude: null },

      expandedHoleId: null,
      openSections: {},
      holeTees: {},
      newTee: { name: '', distanceM: 0, areaM2: null },

      fertilizerProducts: [],
      newFertilizerProductName: '',

      showCreateStation: false,
      editingStation: null,
      stationForm: this.emptyStationForm(),

      defaultCourseId: null,
      savingDefault: false,
    };
  },
  watch: {
    selectedCourseId() {
      this.fetchHoles();
    },
  },
  async mounted() {
    this.fetchData();
    const val = await getCustomerConfig('DefaultGolfCourseId');
    this.defaultCourseId = val ? Number(val) : null;
  },
  methods: {
    emptyStationForm() {
      return { name: '', stationId: '', golfCourseId: null, provider: 1, baseApiUrl: 'https://api.weather.com', apiKey: '', geocode: '', isActive: true };
    },

    onProviderChange() {
      // Reset base URL to provider default when user switches provider
      if (this.stationForm.provider === 2) {
        this.stationForm.baseApiUrl = 'https://api.vedur.is';
        this.stationForm.apiKey = '';
        this.stationForm.geocode = '';
      } else if (this.stationForm.provider === 1) {
        this.stationForm.baseApiUrl = 'https://api.weather.com';
      }
    },

    providerLabel(provider) {
      if (provider === 2) return this.$t('golf.weatherProviderVedur');
      return this.$t('golf.weatherProviderWeatherCompany');
    },

    async fetchData() {
      try {
        const [coursesRes, stationsRes, productsRes] = await Promise.all([
          api.get('/courses'),
          api.get('/weather-stations'),
          api.get('/fertilizer-products'),
        ]);
        this.courses = coursesRes.data;
        this.stations = stationsRes.data;
        this.fertilizerProducts = productsRes.data;
        if (this.courses.length > 0 && !this.selectedCourseId) {
          this.selectedCourseId = this.courses[0].id;
        }
      } catch (error) {
        console.error('Failed to load golf settings:', error); // eslint-disable-line no-console
      }
    },

    async fetchHoles() {
      if (!this.selectedCourseId) { this.holes = []; return; }
      try {
        const res = await api.get(`/holes?courseId=${this.selectedCourseId}`);
        this.holes = res.data.map(h => ({
          ...h,
          _name: h.name,
          _par: h.par,
          _handicapIndex: h.handicapIndex,
          _greenAreaM2: h.greenAreaM2,
          _fairwayAreaM2: h.fairwayAreaM2,
          _fairwayLengthM: null,
          _teeCount: h.teeCount,
        }));
      } catch (error) {
        console.error('Failed to load holes:', error); // eslint-disable-line no-console
      }
    },

    async createCourse() {
      try {
        await api.post('/courses', this.newCourse);
        this.$toast.success(this.$t('golf.courseCreated'));
        this.showCreateCourse = false;
        this.newCourse = { name: '', numberOfHoles: 18, latitude: null, longitude: null };
        this.fetchData();
      } catch (error) {
        console.error('Failed to create course:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    async initializeHoles(course) {
      try {
        await api.post('/holes/initialize', { golfCourseId: course.id, numberOfHoles: course.numberOfHoles });
        this.$toast.success(`${course.numberOfHoles} ${this.$t('golf.holesCreated')}`);
        this.fetchData();
        this.selectedCourseId = course.id;
      } catch (error) {
        console.error('Failed to initialize holes:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    async updateHole(h) {
      try {
        await api.put(`/holes/${h.id}`, {
          holeId: h.id,
          name: h._name,
          par: h._par,
          handicapIndex: h._handicapIndex,
          greenAreaM2: h._greenAreaM2,
          fairwayAreaM2: h._fairwayAreaM2,
          fairwayLengthM: h._fairwayLengthM,
        });
      } catch (error) {
        console.error('Failed to update hole:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    toggleHole(hole) {
      this.expandedHoleId = this.expandedHoleId === hole.id ? null : hole.id;
    },

    toggleSection(hole, section) {
      const key = `${hole.id}-${section}`;
      this.openSections = { ...this.openSections, [key]: !this.openSections[key] };
    },

    isSectionOpen(hole, section) {
      return !!this.openSections[`${hole.id}-${section}`];
    },

    async loadTees(hole) {
      if (this.holeTees[hole.id]) return;
      try {
        const res = await api.get(`/holes/${hole.id}`);
        this.holeTees = { ...this.holeTees, [hole.id]: res.data.tees || [] };
      } catch (error) {
        console.error('Failed to load tees:', error); // eslint-disable-line no-console
      }
    },

    async createTee(hole) {
      if (!this.newTee.name || !this.newTee.distanceM) return;
      try {
        await api.post('/tees', {
          golfHoleId: hole.id,
          name: this.newTee.name,
          distanceM: this.newTee.distanceM,
          areaM2: this.newTee.areaM2,
        });
        this.$toast.success(this.$t('golf.teeCreated'));
        this.newTee = { name: '', distanceM: 0, areaM2: null };
        this.holeTees = { ...this.holeTees, [hole.id]: null };
        this.loadTees(hole);
        this.fetchHoles();
      } catch (error) {
        console.error('Failed to create tee:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    async deleteTee(hole, tee) {
      try {
        await api.delete(`/tees/${tee.id}`);
        this.holeTees = { ...this.holeTees, [hole.id]: (this.holeTees[hole.id] || []).filter(t => t.id !== tee.id) };
        this.fetchHoles();
      } catch (error) {
        console.error('Failed to delete tee:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    async createFertilizerProduct() {
      if (!this.newFertilizerProductName.trim()) return;
      try {
        await api.post('/fertilizer-products', { name: this.newFertilizerProductName.trim() });
        this.$toast.success(this.$t('common.created'));
        this.newFertilizerProductName = '';
        const res = await api.get('/fertilizer-products');
        this.fertilizerProducts = res.data;
      } catch (error) {
        console.error('Failed to create fertilizer product:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    async deleteFertilizerProduct(product) {
      try {
        await api.delete(`/fertilizer-products/${product.id}`);
        this.fertilizerProducts = this.fertilizerProducts.filter(p => p.id !== product.id);
      } catch (error) {
        console.error('Failed to delete fertilizer product:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    editStation(s) {
      this.editingStation = s;
      this.showCreateStation = false;
      this.stationForm = {
        name: s.name,
        stationId: s.stationId,
        golfCourseId: s.golfCourseId,
        provider: s.provider || 1,
        baseApiUrl: s.baseApiUrl,
        apiKey: '',
        geocode: s.geocode || '',
        isActive: s.isActive,
      };
    },

    cancelStation() {
      this.showCreateStation = false;
      this.editingStation = null;
      this.stationForm = this.emptyStationForm();
    },

    async saveStation() {
      try {
        if (this.editingStation) {
          await api.put(`/weather-stations/${this.editingStation.id}`, this.stationForm);
        } else {
          await api.post('/weather-stations', this.stationForm);
        }
        this.$toast.success(this.$t('golf.stationSaved'));
        this.cancelStation();
        this.fetchData();
      } catch (error) {
        console.error('Failed to save station:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    async saveDefaultCourse() {
      this.savingDefault = true;
      try {
        await mainApi.post('/Customers/CustomerConfigurations', {
          configurations: [{
            customerConfigurationTypeId: getCustomerConfigTypeId('DefaultGolfCourseId'),
            value: this.defaultCourseId ? String(this.defaultCourseId) : '',
          }],
        });
        await loadCustomerConfigs();
        this.$toast.success(this.$t('golf.defaultCourseSaved'));
      } catch (error) {
        console.error('Failed to save default course:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.savingDefault = false;
      }
    },

    courseNameById(id) {
      if (!id) return '–';
      return this.courses.find(c => c.id === id)?.name || '–';
    },
  },
};
</script>

<style scoped>
.btn-xs {
  padding: 0.15rem 0.4rem;
  font-size: 0.75rem;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}

.form-control,
.form-select {
  border: 1px solid #d2d6da !important;
}

.form-control:focus,
.form-select:focus {
  border-color: #4caf50 !important;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2) !important;
}
</style>
